import type { FocusAskTargetDateValue } from '@atlassian/conversation-assistant-message-actions-api/focus-ask';

/** Formatted like "YYYY-MM-DD". */
const ISO_DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses a string to "YYYY-MM-DD".
 */
export function parseStringToIsoDay(raw: string): string {
	const trimmed = raw.trim();
	if (!trimmed) {
		return '';
	}
	const isoMatch = ISO_DAY_ONLY.exec(trimmed);
	if (isoMatch) {
		return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
	}
	return '';
}

/**
 * Parses `YYYY-MM-DD` to a Date object.
 */
export function parseIsoDayToDate(isoDay: string): Date | undefined {
	const trimmed = isoDay.trim();
	const m = ISO_DAY_ONLY.exec(trimmed);
	if (!m) {
		return undefined;
	}
	const y = Number(m[1]);
	const mo = Number(m[2]) - 1;
	const d = Number(m[3]);
	const date = new Date(y, mo, d);
	return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Formats a date to a medium date string.
 * Returns an empty string if the date is not a valid date.
 */
export function formatDatetoMediumString(
	raw: string,
	formatDate: (value: Date | number, opts?: Intl.DateTimeFormatOptions) => string,
): string {
	const trimmed = raw.trim();
	const parsed = parseIsoDayToDate(trimmed);
	if (parsed !== undefined) {
		return formatDate(parsed, { dateStyle: 'medium' });
	}
	return '';
}

/**
 * Reads the calendar / free-text date from wire payloads:
 * plain string, `{ date: string }`, or nested `{ targetDate: string, targetDateType?: 'DAY' }`.
 */
export function extractTargetDateFromRecord(record: Record<string, unknown>): string {
	const td = record.targetDate;
	if (typeof td === 'string') {
		return td;
	}
	if (td && typeof td === 'object' && !Array.isArray(td)) {
		const o = td as Record<string, unknown>;
		if (typeof o.targetDate === 'string') {
			return o.targetDate;
		}
		const date = o.date;
		if (typeof date === 'string') {
			return date;
		}
	}
	return '';
}

/**
 * Turns a raw target date string into a FocusAskTargetDateValue.
 */
export function parseRecordToFocusAskTargetDateValue(
	record: Record<string, unknown>,
): FocusAskTargetDateValue {
	const raw = extractTargetDateFromRecord(record);
	const iso = parseStringToIsoDay(raw);
	if (iso) {
		return { targetDate: iso, targetDateType: 'DAY' };
	}
	return { targetDate: '' };
}

/** Default empty target date. */
export function emptyFocusAskTargetDateValue(): FocusAskTargetDateValue {
	return { targetDate: '' };
}
